import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import withReduxState from '../../hoc/wReduxState';
import WithRoutify from '../../hoc/WithRoutify';
import TableGrid from '../../components/UI/TableGrid';
import FormInputs from '../../components/UI/FormInputs';
import Buttons from '../../components/UI/Buttons';
import RxntService from '../../services/RxntService';
import RxntLaunchButton from '../../components/Rxnt/RxntLaunchButton';
import { fetchRxntSettings, voidPrescription } from '../../store/RxntSlice';
import { OK } from '../../utils/constant';
import PERMISSIONS from '../../utils/AclPermissions';
import {
  canShowVoidForVisitPrescription,
  pendingRxntPrescriptionIdSet,
} from '../../utils/rxntUiUtils';
import { RXNT_VOID_CODE_OPTIONS, RXNT_VOID_CODE_PLACEHOLDER } from '../../utils/rxntVoidCodes';

const VOID_CODE_SELECT_OPTIONS = [RXNT_VOID_CODE_PLACEHOLDER, ...RXNT_VOID_CODE_OPTIONS];

const STATUS_LABELS = {
  PENDING: 'Pending',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  FILLED: 'Filled',
  CANCELLED: 'Cancelled',
  VOIDED: 'Voided',
  FAILED: 'Failed',
};

const TABLE_HEADERS = [
  { label: 'Visit Date', key: 'visit_date', sortName: 'visit_date' },
  { label: 'Medication', key: 'medication', sortName: 'medication' },
  { label: 'Sig', key: 'sig', sortName: 'sig' },
  { label: 'Qty', key: 'quantity', sortName: 'quantity' },
  { label: 'Pharmacy', key: 'pharmacy', sortName: 'pharmacy' },
  { label: 'Provider', key: 'provider_name', sortName: 'provider_name' },
  { label: 'Status', key: 'status', sortName: 'status' },
  { label: 'Prescribed', key: 'date_display', sortName: 'date_display' },
  { label: 'Action', key: 'action', type: 'Actions' },
];

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 25,
  totalRecords: 0,
  sort_by: 'date_display',
  sort_order: 'desc',
};

function getSortValue(prescription, sortBy) {
  switch (sortBy) {
    case 'visit_date': {
      const v = prescription?.visit;
      const raw = [v?.visit_date, v?.visit_time].filter(Boolean).join(' ').trim();
      const ts = raw ? Date.parse(raw) : NaN;
      return Number.isFinite(ts) ? ts : 0;
    }
    case 'medication':
      return [prescription.drug_name, prescription.drug_strength].filter(Boolean).join(' ').toLowerCase();
    case 'sig':
      return (prescription.sig || '').toLowerCase();
    case 'quantity':
      return Number(prescription.quantity) || 0;
    case 'pharmacy':
      return (prescription.pharmacy_name || '').toLowerCase();
    case 'provider_name': {
      const p = prescription.provider;
      return [p?.first_name, p?.last_name].filter(Boolean).join(' ').toLowerCase();
    }
    case 'status':
      return (prescription.status || '').toLowerCase();
    case 'date_display':
    default: {
      const ts = Date.parse(prescription.sent_at || prescription.created_at || '');
      return Number.isFinite(ts) ? ts : 0;
    }
  }
}

function comparePrescriptions(a, b, sortBy, sortOrder) {
  const va = getSortValue(a, sortBy);
  const vb = getSortValue(b, sortBy);
  let cmp = 0;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  }
  return sortOrder === 'asc' ? cmp : -cmp;
}

function prescriptionSearchBlob(prescription) {
  const p = prescription?.provider;
  const providerName = [p?.first_name, p?.last_name].filter(Boolean).join(' ');
  const statusLabel = STATUS_LABELS[prescription.status] || prescription.status || '';
  return [
    visitDateDisplay(prescription),
    prescription.drug_name,
    prescription.drug_strength,
    prescription.sig,
    prescription.pharmacy_name,
    providerName,
    statusLabel,
    prescription.quantity,
    prescription.quantity_unit,
    formatDate(prescription.sent_at || prescription.created_at),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function formatDate(dateString) {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function visitDateDisplay(prescription) {
  const v = prescription?.visit;
  if (v?.visit_date) {
    const t = v.visit_time ? ` ${v.visit_time}` : '';
    return `${v.visit_date}${t}`.trim();
  }
  return '—';
}

const RxntPrescriptionHistory = ({ employee_id, patientDetails, Toast, Loader }) => {
  const dispatch = useDispatch();
  const { settings } = useSelector((s) => s.rxnt);
  const ERROR_TOAST_TIMEOUT = 6000;

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION });
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printImage, setPrintImage] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [printFor, setPrintFor] = useState(null);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidComments, setVoidComments] = useState('');
  const [voidCode, setVoidCode] = useState('');
  const [voidFor, setVoidFor] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingRxntIds, setPendingRxntIds] = useState(null);

  const employeeId = employee_id || patientDetails?.employee_id;

  const providerDoctorPairs = useMemo(() => {
    const seen = new Set();
    const pairs = [];
    rows.forEach((p) => {
      if (!p.provider_id || p.rxnt_doctor_id == null) return;
      const key = `${p.provider_id}:${p.rxnt_doctor_id}`;
      if (seen.has(key)) return;
      seen.add(key);
      pairs.push({
        provider_id: p.provider_id,
        rxnt_doctor_id: Number(p.rxnt_doctor_id),
      });
    });
    return pairs;
  }, [rows]);

  const rxntHistoryLaunch = useMemo(() => {
    const ref = rows.find((p) => p.rxnt_patient_id != null) ?? rows[0];
    const pair = providerDoctorPairs[0];
    const rxntPatientId = ref?.rxnt_patient_id != null ? Number(ref.rxnt_patient_id) : null;
    const launchDoctorId =
      pair?.rxnt_doctor_id ??
      (ref?.rxnt_doctor_id != null ? Number(ref.rxnt_doctor_id) : null);
    return {
      hasPrescription: rows.length > 0,
      launchProviderId:
        pair?.provider_id ||
        (ref?.provider_id != null ? String(ref.provider_id) : ''),
      launchDoctorId,
      rxntPatientId: Number.isFinite(rxntPatientId) && rxntPatientId > 0 ? rxntPatientId : null,
    };
  }, [rows, providerDoctorPairs]);

  const loadHistory = useCallback(async () => {
    if (!employeeId) {
      setRows([]);
      return;
    }
    setError(null);
    if (Loader) Loader(true);
    try {
      const res = await RxntService.getPrescriptionHistory(employeeId, 500);
      if (res?.code === OK) {
        const list = Array.isArray(res.data) ? res.data : [];
        setRows(list);
      } else {
        setError(res?.message || 'Failed to load prescription history');
        setRows([]);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load prescription history');
      setRows([]);
    } finally {
      if (Loader) Loader(false);
    }
  }, [employeeId, Loader]);

  useEffect(() => {
    dispatch(fetchRxntSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings.loading) return;
    if (!settings.rx_enabled) {
      setRows([]);
      return;
    }
    loadHistory();
  }, [settings.loading, settings.rx_enabled, loadHistory]);

  useEffect(() => {
    if (!settings.rx_enabled || providerDoctorPairs.length === 0) {
      setPendingRxntIds(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = new Set();
        const results = await Promise.all(
          providerDoctorPairs.map(({ provider_id, rxnt_doctor_id }) =>
            RxntService.listPrescriptions({
              tab: 'pending',
              provider_id,
              rxnt_doctor_id,
              per_page: 300,
            })
          )
        );
        results.forEach((res) => {
          if (res?.code === OK) {
            pendingRxntPrescriptionIdSet(res.data).forEach((id) => merged.add(id));
          }
        });
        if (!cancelled) setPendingRxntIds(merged);
      } catch {
        if (!cancelled) setPendingRxntIds(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings.rx_enabled, providerDoctorPairs]);

  const handleVoidClick = (prescription) => {
    setVoidFor(prescription);
    setVoidComments('');
    setVoidCode('');
    setVoidModalOpen(true);
  };

  const handleVoidConfirm = async () => {
    if (!voidCode) {
      if (Toast) Toast.error('Please select a void reason', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!voidComments.trim()) {
      if (Toast) Toast.error('Please enter comments for voiding', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!voidFor?.id) return;

    setActionLoading(true);
    try {
      await dispatch(
        voidPrescription({
          prescriptionId: voidFor.id,
          comments: voidComments.trim(),
          voidCode,
        })
      ).unwrap();
      if (Toast) Toast.success('Prescription voided successfully');
      setVoidModalOpen(false);
      setVoidFor(null);
      loadHistory();
    } catch (error) {
      if (Toast) {
        Toast.error(
          typeof error === 'string' ? error : error?.message || 'Failed to void prescription',
          ERROR_TOAST_TIMEOUT
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async (prescription) => {
    if (!prescription?.rxnt_prescription_id) {
      if (Toast) Toast.error('Not yet sent to RXNT', ERROR_TOAST_TIMEOUT);
      return;
    }
    setPrintLoading(true);
    setPrintFor(prescription);
    if (Loader) Loader(true);
    try {
      const response = await RxntService.printPrescription(prescription.id);
      if (response?.code === OK && response.data?.image) {
        setPrintImage(response.data.image);
        setPrintModalOpen(true);
      } else if (Toast) {
        Toast.error(response?.message || 'Print failed', ERROR_TOAST_TIMEOUT);
      }
    } catch (err) {
      if (Toast) {
        Toast.error(err?.message || 'Print failed', ERROR_TOAST_TIMEOUT);
      }
    } finally {
      setPrintLoading(false);
      if (Loader) Loader(false);
    }
  };

  const applySearchHandler = useCallback(() => {
    setAppliedSearch((searchInput || '').trim());
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchInput]);

  const clearSearchHandler = useCallback(() => {
    setSearchInput('');
    setAppliedSearch('');
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const searchKeyDownHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applySearchHandler();
    }
  };

  const handleTablePagination = useCallback((newPagination) => {
    setPagination((prev) => ({
      ...prev,
      ...newPagination,
    }));
  }, []);

  const handleClosePrintModal = () => {
    setPrintModalOpen(false);
    setPrintImage(null);
    setPrintFor(null);
  };

  const handleDownloadPrint = () => {
    if (!printImage || !printFor) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${printImage}`;
    link.download = `prescription_${printFor.drug_name || 'rx'}_${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBrowserPrint = () => {
    if (!printImage) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      if (Toast) Toast.error('Pop-up blocked. Allow pop-ups to print.', ERROR_TOAST_TIMEOUT);
      return;
    }
    printWindow.document.write(`
      <html>
        <head><title>Prescription - ${printFor?.drug_name || 'Print'}</title></head>
        <body style="margin:0; display:flex; justify-content:center; align-items:center;">
          <img src="data:image/png;base64,${printImage}" style="max-width:100%;" onload="window.print();window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const filteredRows = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((prescription) => prescriptionSearchBlob(prescription).includes(q));
  }, [rows, appliedSearch]);

  const sortedRows = useMemo(() => {
    const sortBy = pagination.sort_by || 'date_display';
    const sortOrder = pagination.sort_order || 'desc';
    return [...filteredRows].sort((a, b) => comparePrescriptions(a, b, sortBy, sortOrder));
  }, [filteredRows, pagination.sort_by, pagination.sort_order]);

  useEffect(() => {
    const perPage = pagination.perPage || 25;
    const totalPages = Math.max(1, Math.ceil(sortedRows.length / perPage) || 1);
    if ((pagination.page || 1) > totalPages) {
      setPagination((prev) => ({ ...prev, page: totalPages }));
    }
  }, [sortedRows.length, pagination.page, pagination.perPage]);

  const pagedRows = useMemo(() => {
    const perPage = pagination.perPage || 25;
    const page = pagination.page || 1;
    const start = (page - 1) * perPage;
    return sortedRows.slice(start, start + perPage);
  }, [sortedRows, pagination.page, pagination.perPage]);

  const tableRecords = useMemo(() => {
    const tableData = pagedRows.map((prescription) => {
      const drugLine = [prescription.drug_name, prescription.drug_strength]
        .filter(Boolean)
        .join(' · ');
      const providerName = [prescription.provider?.first_name, prescription.provider?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || '—';
      const qty =
        prescription.quantity != null
          ? `${prescription.quantity}${prescription.quantity_unit ? ` ${prescription.quantity_unit}` : ''}`
          : '—';

      const actionBtns = [];
      if (canShowVoidForVisitPrescription(prescription, pendingRxntIds)) {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Remove',
          title: 'Void',
          acl: PERMISSIONS.RESULT.LIST,
          disabled: actionLoading,
          clickHandler: () => handleVoidClick(prescription),
        });
      }
      if (prescription.rxnt_prescription_id && prescription.status !== 'PENDING') {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Print',
          title: 'Print',
          acl: PERMISSIONS.RESULT.LIST,
          disabled: printLoading || actionLoading,
          clickHandler: () => handlePrint(prescription),
        });
      }

      return {
        isHeading: false,
        rowId: `rxnt-hist-${prescription.id}`,
        data: {
          visit_date: visitDateDisplay(prescription),
          medication: drugLine || '—',
          sig: prescription.sig || '—',
          quantity: qty,
          pharmacy: prescription.pharmacy_name || '—',
          provider_name: providerName,
          status: STATUS_LABELS[prescription.status] || prescription.status || '—',
          date_display: formatDate(prescription.sent_at || prescription.created_at),
          action: actionBtns,
        },
      };
    });

    const totalRecords = sortedRows.length;
    const perPage = pagination.perPage || 25;
    const totalPages = Math.max(1, Math.ceil(totalRecords / perPage) || 1);
    const page = Math.min(pagination.page || 1, totalPages);

    return {
      tableHeaders: TABLE_HEADERS,
      tableRows: {
        status: 200,
        type: 'default',
        data: tableData,
      },
      pagination: {
        page,
        perPage,
        totalRecords,
        sort_by: pagination.sort_by || 'date_display',
        sort_order: pagination.sort_order || 'desc',
      },
    };
  }, [pagedRows, sortedRows.length, pagination, printLoading, actionLoading, pendingRxntIds]);

  const gridEditProps = useMemo(
    () => ({ activeEditId: null, onChangeFormDataInEdit: () => {} }),
    []
  );

  const patientLabel =
    patientDetails?.patientName ||
    [patientDetails?.first_name, patientDetails?.last_name].filter(Boolean).join(' ') ||
    '';

  if (settings.loading) {
    return null;
  }

  if (!settings.rx_enabled) {
    return (
      <div className="text-center text-muted py-4 px-3">
        <p className="mb-0">RXNT ePrescribing is not enabled for this organization.</p>
      </div>
    );
  }

  return (
    <>
      <div className="sh_cardHeader sh_cardbglight">
        <div className="form-group d-flex flex-wrap mb-0 justify-content-between align-items-center">
          <h6 className="text-sm mb-0 font-weight-bold">
            Prescriptions — all visits{patientLabel ? ` · ${patientLabel}` : ''}
            {appliedSearch ? (
              <span className="text-muted font-weight-normal ml-1">
                ({sortedRows.length} of {rows.length})
              </span>
            ) : null}
          </h6>
          <div className="d-flex align-items-center flex-wrap">
            <div className="form-group-fields row mr-2 mb-0">
              <div className="col-12 px-1 d-flex">
                <div className="btn-group">
                  <div className="input-group input-group-joined input-group-solid">
                    <FormInputs
                      type="TextInput"
                      placeholder="Search"
                      name="rxnt_history_search"
                      value={searchInput}
                      changeHandler={(val) => setSearchInput(val || '')}
                      onKeyDownHandler={searchKeyDownHandler}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="btn-group pl-2 pt-1">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={applySearchHandler}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={clearSearchHandler}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
            <div className="btn-group pt-1">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={loadHistory}
              >
                Refresh
              </button>
              <RxntLaunchButton
                text="Open RXNT"
                linkTarget="patient"
                providerId={rxntHistoryLaunch.launchProviderId}
                rxntDoctorId={rxntHistoryLaunch.launchDoctorId}
                employeeId={employeeId || null}
                rxntPatientId={rxntHistoryLaunch.rxntPatientId}
                disabled={
                  !rxntHistoryLaunch.hasPrescription ||
                  !rxntHistoryLaunch.launchProviderId ||
                  rxntHistoryLaunch.launchDoctorId == null
                }
                disabledTitle={
                  !rxntHistoryLaunch.hasPrescription
                    ? 'No prescriptions in history for this patient'
                    : 'Prescriber is not mapped to RXNT'
                }
                onError={(message) => {
                  if (Toast) Toast.error(message, ERROR_TOAST_TIMEOUT);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-warning py-2 mx-1 small mb-0">{error}</div>}

      <div className="sh_cardBody p-0 sh_cardbglight border-top">

        <TableGrid
          isSort
          hasPagination={
            tableRecords.pagination.totalRecords > tableRecords.pagination.perPage
          }
          needCardBody={false}
          fetchTableRecords={handleTablePagination}
          gridEditProps={gridEditProps}
          customClass="rxnt-patient-history-table"
          {...tableRecords}
        />
      </div>

      <Modal
        show={voidModalOpen}
        onHide={() => {
          setVoidModalOpen(false);
          setVoidFor(null);
        }}
        centered
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">Void Prescription</h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You are about to void the prescription for{' '}
              <strong>
                {[voidFor?.drug_name, voidFor?.drug_strength].filter(Boolean).join(' · ') || '—'}
              </strong>
              .
            </p>
            <div className="form-group">
              <label className="mb-1 text-sm opacity-75">
                Void reason <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-control-solid"
                value={voidCode}
                onChange={(e) => setVoidCode(e.target.value)}
                disabled={actionLoading}
              >
                {VOID_CODE_SELECT_OPTIONS.map((opt) => (
                  <option key={opt.value || 'placeholder'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="mb-1 text-sm opacity-75">
                Comments <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control form-control-solid"
                value={voidComments}
                onChange={(e) => setVoidComments(e.target.value)}
                placeholder="Enter additional comments for voiding this prescription..."
                rows={3}
                disabled={actionLoading}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-secondary sh_btnsecondary mr-2"
              onClick={() => {
                setVoidModalOpen(false);
                setVoidFor(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary sh_btnprimary"
              onClick={handleVoidConfirm}
              disabled={actionLoading || !voidCode || !voidComments.trim()}
            >
              {actionLoading ? 'Voiding...' : 'Void Prescription'}
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={printModalOpen}
        onHide={handleClosePrintModal}
        centered
        size="lg"
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">
                Prescription - {printFor?.drug_name}
              </h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {printImage ? (
              <img
                src={`data:image/png;base64,${printImage}`}
                alt="Prescription"
                style={{ maxWidth: '100%', maxHeight: '70vh', border: '1px solid #ddd' }}
              />
            ) : (
              <div>Loading prescription image...</div>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Buttons
              className="btn btn-secondary sh_btnsecondary mr-2"
              label="Close"
              clickHandler={handleClosePrintModal}
            />
            <Buttons
              className="btn btn-primary sh_btnprimary mr-2"
              label="Download"
              clickHandler={handleDownloadPrint}
              disabled={!printImage}
              acl={PERMISSIONS.RESULT.LIST}
            />
            <Buttons
              className="btn btn-primary sh_btnprimary"
              label="Print"
              clickHandler={handleBrowserPrint}
              disabled={!printImage}
              acl={PERMISSIONS.RESULT.LIST}
            />
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default withReduxState(WithRoutify(RxntPrescriptionHistory));
